<!--

Copyright Paul B. Lucas (C) 2025, 2026   ALL RIGHTS RESERVED.
Licensed to the University of Illinois Urbana Champaign

This component is rendered when invoked from a list of employees under admin privileges.  This allows a
privileged user to view/edit the employee record.    It is primarily used to set employee supervisor info
and to modify the eligibility criteria of an employee.  Contacts may also be added or modified.


Revisions:
    02/28/2025      pblucas     initial creation modified from RWA
 
-->
<template> 
 
    <div id="_pdf_render" class="_master_container"> 
        <!-- first row - full name of employee being edited - computed proxy -->
        <div id="_pdf_row1" class="_container_r1" >
            <div class="_fieldset" style="order: 1;">
                <div v-if="!config['suppress-photos']  && !employee['req-photo-suppress']">
                    <img class="_employee_image" width="300" :src="employee.image">
                </div>
                <div v-else>
                    <img class="_employee_image" width="300" src="@/assets/defaultPhoto.png" >
                </div>
                <div>
                    <p class="_p_annotation_3">UUID: <span class="_p_diminished"> {{ employee.uuid }}</span></p>
                    <p class="_p_annotation_3">MS OID: <span class="_p_diminished"> {{ employee['ms-object-id'] }}</span></p>
                </div>
            </div>
            <div class="_fieldset" style="order: 2;">
                <p class="_p_employee_name">{{ employee['name-concat'] }}</p>
            </div>
        </div>
        <!-- second row is two fieldset columns: PID and home address -->
        <div class="_container_r2" >
            <div id="prop_set_1" class="_fieldset" style="order: 1;">
                <p class="_p_fieldset_name">Personal Identifying Information</p>
                <div class="_row">
                    <p class="_p_annotation_2">Values which are intrinsic to an employee's identity and present in
                        authentication claims are non-editable and disabled.
                    </p>
                </div>
                <div class="_fieldset_box">
                    <div class="_row">
                        <div class="_col_40">
                            <label class="_input_label" for="set1_prop1">First Name</label>
                        </div>
                        <div class="_col_60">
                            <input disabled id="set1_prop1" class="_input" 
                                aria-label="Input first name"
                                aria-describedby="Disable input for first name"   
                                v-model="employee['first-name']" />
                        </div>
                    </div>
                    <div class="_row">
                        <div class="_col_40">
                            <label class="_input_label" for="set1_prop2">Last Name</label>
                        </div>
                        <div class="_col_60">
                            <input disabled id="set1_prop2" class="_input" 
                                aria-label="Input last name"
                                aria-describedby="Disable input for last name"   
                                v-model="employee['last-name']" />
                        </div>
                    </div>
                    <div class="_row">
                        <div class="_col_40">
                            <label class="_input_label" for="set1_prop3">Middle Initial</label>
                        </div>
                        <div class="_col_60">
                            <input disabled id="set1_prop3" class="_input" 
                                aria-label="Input middle initial"
                                aria-describedby="Disable input for middle initial"   
                                v-model="employee['middle-initial']" />
                        </div>
                    </div>
                    <div class="_row">
                        <div class="_col_40">
                            <label class="_input_label" for="set1_prop4">Netid</label>
                        </div>
                        <div class="_col_60">
                            <input disabled id="set1_prop4" class="_input" 
                                aria-label="Input for netid"
                                aria-describedby="Disable input for netid"   
                                v-model="employee['netid']" />
                        </div>
                    </div>
                    <div class="_row">
                        <div class="_col_40">
                            <label class="_input_label" for="set1_prop5">UIN</label>
                        </div>
                        <div class="_col_60">
                            <input disabled id="set1_prop5" class="_input" 
                                aria-label="Input for UIN"
                                aria-describedby="Disable input for UIN"   
                                v-model="employee['uin']" />
                        </div>
                    </div>
                    <div class="_row">
                        <div class="_col_40">
                            <label class="_input_label" for="set1_prop6">Job Title</label>
                        </div>
                        <div class="_col_60">
                            <input disabled id="set1_prop6" class="_input" 
                                aria-label="Input for job title"
                                aria-describedby="Disable input for job title"   
                                v-model="employee['title']" />
                        </div>
                    </div>
                    <div class="_row">
                        <div class="_col_40">
                            <label class="_input_label" for="set1_prop7">E Class Code</label>
                        </div>
                        <div class="_col_60">
                            <input id="set1_prop7" class="_input" 
                                aria-label="Input for eclass code"
                                aria-describedby="Input for eclass code"    
                                tabindex = "0" 
                                v-model="employee['eclass-code']" />
                        </div>
                    </div>
                    <div class="_row">
                        <div class="_col_40">
                            <label class="_input_label" for="set1_prop8">Home Org</label>
                        </div>
                        <div class="_col_60">
                            <input id="set1_prop8" class="_input" 
                                aria-label="Input for home orgcode"
                                aria-describedby="Input for hone orgcode"    
                                tabindex = "0" 
                                v-model="employee['home-org']" />
                        </div>
                    </div>
                    <div class="_row">
                        <div class="_col_40">
                            <label class="_input_label" for="set1_prop9">Campus Mail Code</label>
                        </div>
                        <div class="_col_60">
                            <input id="set1_prop9" class="_input" 
                                aria-label="Input for campus mail code"
                                aria-describedby="Input for campus mail code"    
                                tabindex = "0" 
                                v-model="employee['mailcode']" />
                        </div>
                    </div>
                 </div>
            </div>
            <div id="prop_set_2" class="_fieldset" style="order: 2;">
                <p class="_p_fieldset_name">Home Address</p>
                <p class="_p_annotation_2">Home address is address of record in Banner.  Edit with caution. <br /><br />
                </p>
                <div class="_fieldset_box">
                    <div class="_row">
                        <div class="_col_40">
                            <label class="_input_label" for="set2_prop1">Address 1</label>
                        </div>
                        <div class="_col_60">
                            <input id="set2_prop1" class="_input" 
                                aria-label="Input for employee home street address line 1"
                                aria-describedby="Input box for employee home street address line 1"    
                                tabindex = "0" 
                                v-model="employee['address1']" />
                        </div>
                    </div>
                    <div class="_row">
                        <div class="_col_40">
                            <label class="_input_label" for="set2_prop2">Address 2</label>
                        </div>
                        <div class="_col_60">
                            <input id="set2_prop2" class="_input" 
                                aria-label="Input for employee home street address line 2"
                                aria-describedby="Input box for employee home street address line 2"    
                                tabindex = "0" 
                                v-model="employee['address2']" />
                        </div>
                    </div>
                    <div class="_row">
                        <div class="_col_40">
                            <label class="_input_label" for="set2_prop3">City</label>
                        </div>
                        <div class="_col_60">
                            <input id="set2_prop3" class="_input" 
                                aria-label="Input for employee home city"
                                aria-describedby="Input box for employee home city"    
                                tabindex = "0" 
                                v-model="employee['city']" />
                        </div>
                    </div>
                    <div v-if="employee['country'] != 'United States' && employee['country'] != 'USA'">
                        <div class="_row">
                            <div class="_col_40">
                                <label class="_input_label" for="set2_prop4">State</label>
                            </div>
                            <div class="_col_60">
                                <input id="set2_prop4" class="_input" v-model="employee['state']"  
                                    aria-label="State/Territory input for non-USA"   
                                    aria-description="Dropdown list to select state or territory if not in  USA"   
                                />
                            </div>
                        </div>
                    </div>
                    <div v-else>
                        <div class="_row">
                            <div class="_col_40">
                                <label class="_input_label" for="set2_prop4">State</label>
                            </div>
                            <div class="_col_60">
                                <select id="set2_prop4" class="_select" v-model="employee['state']">
                                    <option id="set2_prop4" class="_type_list" v-for="(item, idx) in aStates" 
                                        aria-label="State/Territory selection options" 
                                        aria-description="Dropdown list to select state or territory if in USA"
                                        :value="item"> {{item}} 
                                    </option>
                                </select>
                            </div>
                        </div>
                    </div>
                    <div class="_row">
                        <div class="_col_40">
                            <label class="_input_label" for="set2_prop5">Postcode (Zipcode)</label>
                        </div>
                        <div class="_col_60">
                            <input id="set2_prop5" class="_input" 
                                aria-label="Input for employee home postal code"
                                aria-describedby="Input box for employee home postal code"    
                                tabindex = "0" 
                                v-model="employee['postcode']" />
                        </div>
                    </div>
                    <div class="_row">
                        <div class="_col_40">
                            <label class="_input_label" for="set2_prop6">Country</label>
                        </div>
                        <div class="_col_60">
                            <input id="set2_prop6" class="_input" 
                                aria-label="Input for employee home country"
                                aria-describedby="Input box for employee home country"    
                                tabindex = "0" 
                                v-model="employee['country']" />
                        </div>
                    </div>
                    <div class="_row">
                        <div class="_col_40">
                            <label class="_input_label" for="set2_prop7">Timezone</label>
                        </div>
                        <div class="_col_60">
                            <select v-model="employee['timezone']" class="_select"
                                aria-label="Select list for employee home timezone"
                                aria-describedby="Selection for employee home timezone"    
                                tabindex = "0" >
                                <option id="set2_prop7" class="_type_list" disabled> {{employee['timezone']}} </option>
                                <option id="set2_prop7" class="_type_list" v-for="(zone, idx) in aTimezones" :value="zone.zone" > {{zone.zone}} </option>
                            </select>
                        </div>
                    </div>
                    <br />
                    <p class="_p_annotation">Miscellaneous</p>
                    <div class="_row">
                        <div class="_col_8">
                            <input id="set2_prop8" class="_check" type="checkbox" 
                                aria-label="Check to suppress employee photo"
                                aria-describedby="Checkbox for suppression of employee's photo"    
                                tabindex = "0" 
                                v-model="employee['req-photo-suppress']" value=true>
                        </div>
                        <div class="_col_90">
                            <label class="_check_label" for="set2_prop8">Employee requests photo to be suppressed</label>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <!-- third row is two fieldset columns, employee contacts, and supervisor -->
        <div class="_container_r3" >
            <div id="prop_set_3" class="_fieldset" style="order: 1;">
                <p class="_p_fieldset_name">Contacts</p>
                <div class="_fieldset_box" style="min-height: 90px;">
                    <!--  display any contacts already loaded -->          
                    <div class="_contacts_box"  v-if="employee.contacts.length > 0">
                        <div class="_row" v-for="(_,idx)  in employee.contacts">
                            <div class="_col_40" style=" overflow-x: hidden;">
                                <p class="_p_contact">{{ employee.contacts[idx].type }} </p>
                            </div>
                            <div class="_col_40" style="overflow-x: hidden;">
                                <p class="_p_contact"> {{ employee.contacts[idx].value}} </p>
                            </div>
                            <div class="_col_20" >
                                    <button class="_contact_button_delete" @click="deleteContact(employee, idx)"
                                        aria-label="Click to delete listed contact"
                                        aria-describedby="Click to remove contact from contact list then save"    
                                        tabindex = "0">Delete
                                    </button>                               
                            </div>
                        </div>
                    </div>  
                    <br />
                    <div class="_contacts_add_request" v-if="!bViewContactEntryForm" >
                        <button class="_contact_button_add" @click="toggleContactEntryForm"
                            aria-label="Click to add a new contact"
                            aria-describedby="Click to open new contact entry form"    
                            tabindex = "0">Add A Contact
                        </button>
                    </div> 
                    <form  class='_form' v-if="bViewContactEntryForm">   
                        <div class="_row">    
                            <div class="_scol_35">
                                <label class="_input_label" for="set3_prop1">Type</label>
                            </div>
                            <div class="_scol_50">
                                <select id="set3_prop1" class="_select" 
                                    aria-label="Select contact type"
                                    aria-describedby="Selection list for contact type"    
                                    tabindex = "0" 
                                    v-model="aContact.type" >
                                    <option class="_type_list" v-for="(item, idx) in aContactTypes" :value="item" > {{item}} </option>
                                </select>
                            </div>
                        </div>
                        <div class="_row">
                            <div class="_scol_35">
                                <label class="_input_label" for="set3_prop2">Target</label>
                            </div>
                            <div class="_scol_50">
                                <input id="set3_prop2" class="_input" type="text" 
                                    aria-label="Input contact value, e.g. email address or phone number"
                                    aria-describedby="Input box for contact data per type"    
                                    tabindex = "0" 
                                    v-model="aContact.value" @keydown.enter="addContact(employee,aContact)">
                            </div>
                        </div>
                        <br />
                        <div class="_control_left_align" >
                            <button class="_contact_button_add" @click="addContact(employee,aContact)"
                                aria-label="Click to add new cpntact"
                                aria-describedby="Contact add button"    
                                tabindex = "0">Add This Contact
                            </button>
                            <button class="_contact_button_cancel" @click="cancelAddContact"
                                aria-label="Click to cancel contact addition."
                                aria-describedby="Click to cancel and close new contact form"    
                                tabindex = "0">Cancel
                            </button>
                        </div>
                    </form>
                </div> 
            </div>
        </div>
        <!-- forth row - affiliation  -->
        <div class="_container_r4" >
            <div id="prop_set_5" class="_fieldset" style="order: 1;">
                <p class="_p_fieldset_name">Affiliation</p>
                <p class="_p_annotation">Employee's affiliation is determined by employment contract and should be edited 
                    only by the employee's unit HR representative.
                </p>
                <div class="_fieldset_box">
                   <div class="_row">
                        <div class="_col_40">
                            <label class="_input_label" for="set5_prop1">College</label>
                        </div>
                        <div class="_col_60">
                            <select v-model="codeSelection['college']" class="_select"  @change="updateCollegeCodeSelection"
                                aria-label="Select affiliation college from list"
                                aria-describedby="Selection list for employee's college affiliation"    
                                tabindex = "0">
                                <!-- item['code'] is college code, item ['name'] is name of college -->
                                <option id="set5_prop1" class="_type_list" disabled> {{ codeSelection['college'] }}</option>
                                <option id="set5_prop1" class="_type_list" v-for="(item, idx) in aCollegeCodes" :value="item['name']"> {{ item['name'] }}</option>
                            </select>
                        </div>
                    </div>
                    <div class="_row">
                        <div class="_col_40">
                            <label class="_input_label" for="set5_prop2">College Code</label>
                        </div>
                        <div class="_col_15">
                            <input disabled id="set5_prop2" class="_input"  
                                aria-label="College code"
                                aria-describedby="Disabled input box to display employee college code"    
                                tabindex = "0" 
                                v-model="codeSelection['college-code']" />
                        </div>
                   </div>
                   <div class="_row" v-if="bShowDeptSelection">
                        <div class="_col_40">
                            <label class="_input_label" for="set5_prop3">Department</label>
                        </div>
                        <div class="_col_60">
                            <select v-model="codeSelection['department']" class="_select"  @change="updateDeptCodeSelection" 
                                aria-label="Select department name from list of college's departments"
                                aria-describedby="Input selection for department name. "    
                                tabindex = "0">
                                <!-- item['code'] is college code, item ['name'] is name of college -->
                                <option id="set5_prop3" class="_type_list" disabled> {{ codeSelection['department'] }}</option>
                                <option id="set5_prop3" class="_type_list" v-for="(item, idx) in aFilteredDeptCodes" :value="item['name']"> {{ item['name'] }}</option>
                            </select>
                        </div>
                    </div>
                    <div class="_row" v-if="bShowDeptSelection">
                        <div class="_col_40">
                            <label class="_input_label" for="set5_prop4">Dept Code</label>
                        </div>
                        <div class="_col_15">
                            <input disabled id="set5_prop4" class="_input" v-model="codeSelection['dept-code']" />
                        </div>
                   </div>
                   <div class="_row" v-if="bShowOrgSelection">
                        <div class="_col_40">
                            <label class="_input_label" for="set5_prop5">Div/Org</label>
                        </div>
                        <div class="_col_60">
                            <select v-model="codeSelection['div/org']" class="_select"  @change="updateOrgCodeSelection" 
                                aria-label="Select division code from list"
                                aria-describedby="Input selection list for or employee's divison"    
                                tabindex = "0">
                                <!-- item['code'] is college code, item ['name'] is name of college -->
                                <option id="set5_prop5" class="_type_list" disabled> {{ codeSelection['div/org'] }}</option>
                                <option id="set5_prop5" class="_type_list" v-for="(item, idx) in aFilteredOrgCodes" :value="item['name']"> {{ item['name'] }}</option>
                            </select>
                        </div>
                    </div>
                    <div class="_row" v-if="bShowOrgSelection">
                        <div class="_col_40">
                            <label class="_input_label" for="set5_prop6">Orgcode</label>
                        </div>
                        <div class="_col_15">
                            <input disabled id="set5_prop6" class="_input" v-model="codeSelection['orgcode']" />
                        </div>
                   </div>
                </div>
            </div>
        </div>

        <!-- fifth row, one set of checkboxes for administrator privilege   -->

        <div class="_container_r5" v-if="systemAdministrator">
            <div class="_fieldset" style="order: 1;"  >
                <p class="_p_fieldset_name">System Administration</p>
                <div class="_row">
                    <p class="_p_annotation_2">Changing these values will modify the respective application AD security group (Entra).</p>
                </div>
                <div id="prop_set_8" class="_fieldset_box_admin">
                    
                    <div class="_row" >
                        <div class="_col_8" v-if="employee.uuid == user.uuid">
                            <input disabled id="set8_prop1" class="_check" type="checkbox" v-model="emplIsSysAdmin" value=true @change="$emit('setEmplAsSysAdmin', this.emplIsSysAdmin, this.employee['ms-object-id'])">
                        </div>
                        <div class="_col_8" v-else>
                            <input id="set8_prop1" class="_check" type="checkbox" v-model="emplIsSysAdmin" value=true @change="$emit('setEmplAsSysAdmin', this.emplIsSysAdmin, this.employee['ms-object-id'])">
                        </div>
                        <div class="_col_60">
                            <label class="_check_label" for="set8_prop1">Enable as system administrator</label>
                        </div>
                    </div>
                    <div class="_row">
                        <div class="_col_8">
                            <input id="set8_prop2" class="_check" type="checkbox" v-model="emplIsDeptAdmin" value=true @change="$emit('setEmplAsDeptAdmin',emplIsDeptAdmin,employee['ms-object-id'])">
                        </div>
                        <div class="_col_60">
                            <label class="_check_label" for="set8_prop2">Enable as department administrator</label>  
                        </div>
                    </div>
                    <div class="_row">
                        <div class="_col_8">
                            <input id="set8_prop3" class="_check" type="checkbox" v-model="emplIsAuditor" value=true @change="$emit('setEmplAsAuditor',emplIsDeptAdminAuditor,employee['ms-object-id'])">
                        </div>
                        <div class="_col_60">
                            <label class="_check_label" for="set8_prop3">Enable as unit auditor</label>  
                        </div>
                    </div>
                    <div class="_row">
                        <div class="_col_8">
                            <input id="set8_prop4" class="_check" type="checkbox" v-model="emplIsITSupport" value=true @change="$emit('setEmplAsITSupport',emplIsITSupport,employee['ms-object-id'])">
                        </div>
                        <div class="_col_60">
                            <label class="_check_label" for="set8_prop4">Enable as unit IT support</label>  
                        </div>
                    </div>
                    <div class="_row">
                        <div class="_col_8">
                            <input id="set8_prop5" class="_check" type="checkbox" v-model="emplIsReceiver" value=true @change="$emit('setEmplAsReceiver',emplIsReceiver,employee['ms-object-id'])">
                        </div>
                        <div class="_col_60">
                            <label class="_check_label" for="set8_prop5">Enable as unit asset receiver</label>  
                        </div>
                    </div>
                    <div class="_row">
                        <div class="_col_40">
                            <label class="_input_label" for="set5_prop6">Default Role</label>
                        </div>
                        <div class="_col_60">
                            <select v-model="employee['default-role']" class="_select"  @change="updateDefaultRole" 
                                aria-label="Select default role from list"
                                aria-describedby="Input selection list for or employee's default role"    
                                tabindex = "0">
                                <!-- item['code'] is college code, item ['name'] is name of college -->
                                <option id="set5_prop6" class="_type_list" disabled> {{ emplDefaultRole }}</option>
                                <option id="set5_prop6" class="_type_list" v-for="(item, idx) in aRoles" :value="item"> {{ item }}</option>
                            </select>
                        </div>
                    </div>
                   <div class="_row">
                        <p class="_p_annotation_2">Enter comma delimited list of allowed orgs for admin privileges</p>
                    </div>
                    <div class="_row">
                        <div class="_col_40">
                            <label class="_input_label" for="set8_prop7">List of allowed orgs</label>  
                        </div>
                        <div class="_col_60">
                            <input id="set8_prop7" class="_input" type="text" v-model="sOrgs" @keyup.enter="setAllowedOrgs" @change="setAllowedOrgs">
                       </div>
                    </div>
                </div>   
            </div>            
        </div>

        <!-- clearance for copyright -->
        <br /><br />

    </div>
</template>

<script>
    import { ref, computed }                from 'vue'
    import { useStore }                     from 'vuex'
    import useEmployeesAPI                  from '@/composables/useEmployeesAPI'
    import useAuthAPI                       from '@/composables/useAuthAPI'       
    import useGraphAPI                      from '@/composables/useGraphAPI'

    // calendar date picker widget
    import VueDatePicker                    from '@vuepic/vue-datepicker';
    import '@vuepic/vue-datepicker/dist/main.css';
    import { SemipolarSpinner }             from    'epic-spinners'

    // import enumerations
    import SysConfigs                       from '@/configuration/form-options.json'
    import AdminGroups                      from '@/configuration/adminGroups.config.json'

    export default {
        components: {
            VueDatePicker,
            SemipolarSpinner
        },
        props: [
            'config',
            'employee',         // is deep copy mutatable of filteredEmployees[viewIndex]
            'employees',        // is filteredEmployees list
            'user',             // this employee as signed in user
            'claims',           // claims on active user
            'gat',
            'affiliationCodes'
        ],

        emits: [
            'addEmplContact',
            'deleteEmplContact',
            'setAllowedOrgs',
            'setEmplAsSysAdmin',
            'setEmplAsDeptAdmin',
            'setEmplAsAuditor',
            'setEmplAsITSupport',
            'setEmplAsReceiver',
            'setEmplDefaultRole',
            'setEmplAffiliation',
            'setOID'
        ],
    
        setup(props)    {

            const { getGroupMembers, groupResults, graphError} = useGraphAPI()
       
            // current user's privileges (not indexed employee)  
            const store = useStore()           
            const systemAdministrator   =   computed( () => store.state.user['is-sys-admin'])
            const deptAdministrator     =   computed( () => store.state.user['is-dept-admin'])

            const administrator             =   computed( () => {
                return systemAdministrator.value | deptAdministrator.value 
            })

            // handle enumerations from configuration data
            const sysConfigs            =   ref(SysConfigs)

            // form values for v-models on selected employee
            // these are updated on graph call below.
            const emplIsSysAdmin        =   ref(false)
            const emplIsDeptAdmin       =   ref(false)
            const emplIsReceiver        =   ref(false)
            const emplIsAuditor         =   ref(false)
            const emplIsITSupport       =   ref(false)
            const bGraphError           =   ref(false)

            // Embed function for async usage
            const setUserPermissionsOnGroupMembership = async() =>     {
                const { refreshAccessToken, graphAccessToken, checkAccessToken }    =   useAuthAPI()
                await refreshAccessToken()
                .then ( () =>  {
                    let gat = graphAccessToken.value
                    AdminGroups.securityGroups.forEach( async (g) => {
                        
                        await getGroupMembers(gat, g.id)
                        .then( () => {
                            groupResults.value.forEach( (resp) => {
                                
                                if(resp.userPrincipalName.toString().includes(props.employee.netid)) {
                                    switch(g.role) {
                                        case 'System_Admin':
                                            emplIsSysAdmin.value = true
                                            break;
                                        case 'Dept_Admin':
                                            emplIsDeptAdmin.value = true
                                            break;
                                        case 'Receiver':
                                            emplIsReceiver.value = true
                                            break;
                                        case 'Auditor':
                                            emplIsAuditor.value = true
                                            break;
                                        case 'ITSupport':
                                            emplIsITSupport.value = true
                                            break;
                                   }
                                }
                            })
                        })
                        .catch( () => {
                            bGraphError.value = graphError.value
                        })
                    })
                }) 
            }


            // MARK: Use graph to obtain permissions on employee to set ref value
            //       Iterate all security groups, Request all members of groups,
            //       then filter on userPrincipleName in response
            //  
            setUserPermissionsOnGroupMembership()
            
            // refer to contacts.class.inc.php
            // the following are local objects used to create new contacts
            const aContactTypes         =   ref(sysConfigs.value["contact-types"])        
            const aContact   = ref({
                'type':                     '',      // see type enums from sysConfigs
                'handle':                   '',
                'value':                    '',
                'reference':                ''
            })
          
            // set up the U of I system
            const aCollegeCodes         =   ref([])
            const aDeptCodes            =   ref([])               // name[0], college-code[1], dept-code[2]
            const aOrgCodes             =   ref([])               // name[0], dept-code[1], org[2]
            const bSortByName           =   ref(true)

            aCollegeCodes.value         =   props.affiliationCodes['college-codes']
            aDeptCodes.value            =   props.affiliationCodes["dept-codes"]              // name[0], college-code[1], dept-code[2]
            aOrgCodes.value             =   props.affiliationCodes["org-codes"]               // name[0], dept-code[1], org[2]

            // filtered values for selection process
            const aFilteredDeptCodes    =   ref([])
            const aFilteredOrgCodes     =   ref([])
            const bShowDeptSelection    =   ref(false)
            const bShowOrgSelection     =   ref(false)
            const emplDefaultRole       =   ref(props.employee['default-role'])

            // load selection values from employee prop
            // local values used as modified selections in dropdown lists
            const codeSelection        =   computed( () => {
                let x = {}
                x['college']        =   props.employee['college']
                x['college-code']   =   props.employee['college-code']
                x['department']     =   props.employee['department']
                x['dept-code']      =   props.employee['department-code']
                x['div/org']        =   props.employee['division']
                x['orgcode']        =   props.employee['division-code']
                return x
            })
          

            aCollegeCodes.value.sort((a, b) => {
                var codeA
                var codeB
                if(bSortByName.value) {
                    codeA = a['name']
                    codeB = b['name']
                }
                else {
                    codeA = a['code']
                    codeB = b['code']
                }                        
                if (codeA < codeB) {
                    return -1
                }
                if (codeA > codeB) {
                    return 1
                }
                // codes must be equal
                return 0
            });

            // run the selector filters to establish the correct set of selects on the affiliation selector
            // in case the user decides to edit a pull down
            // Is this the same thing as preLoadAffiliation in onBeforeMount?
            aFilteredDeptCodes.value = aDeptCodes.value.filter ((item) => {return item['college-code'] == codeSelection.value['college-code']})
            aFilteredDeptCodes.value.sort((a, b) => {
                // check department preferences for sorts
                var codeA
                var codeB
                if(bSortByName.value) {
                    codeA = a['name']
                    codeB = b['name']
                }
                else {
                    codeA = a['dept-code']
                    codeB = b['dept-code']
                }               
                if (codeA < codeB) {
                    return -1
                }
                if (codeA > codeB) {
                    return 1
                }
                // names must be equal
                return 0
            });

            // toggle the dept selection form element
            bShowDeptSelection.value = true

            for ( let key in aOrgCodes.value)  {
                let oItem = aOrgCodes.value[key]
                if(oItem['dept-code'] == codeSelection.value['dept-code'])    {
                    aFilteredOrgCodes.value.push(oItem);  
                }
            }
            aFilteredOrgCodes.value.sort((a, b) => {
                var codeA
                var codeB
                if(bSortByName.value) {
                    codeA = a['name']
                    codeB = b['name']
                }
                else {
                    var codeA = a['org']
                    var codeB = b['org']
                }                        
                if (codeA < codeB) {
                    return -1
                }
                if (codeA > codeB) {
                    return 1
                }
                // names must be equal
                return 0
            });

            // toggle the org code selection
            bShowOrgSelection.value = true
            
            // other enumeration lists
            const aTimezones            =   ref(sysConfigs.value['timezones'])
            const aStates               =   ref(sysConfigs.value['states-territories'])
            const aRoles                =   ref(sysConfigs.value['roles'])
      
            const sOrgs                 =   ref(props.employee['allowed-orgs'].toString())               

            return { administrator, systemAdministrator, deptAdministrator, bGraphError,
                     emplIsSysAdmin, emplIsDeptAdmin, emplIsITSupport, emplIsAuditor, emplIsReceiver,
                     sysConfigs, aStates, aTimezones, aRoles, aContactTypes,
                     aOrgCodes,  codeSelection, aCollegeCodes, aDeptCodes, bSortByName, 
                     sOrgs, aContact, aFilteredDeptCodes, aFilteredOrgCodes, emplDefaultRole
                   }
        },

        data() {

            return {
                // temporary working variables for forms
                aUserContacts:          [],             // entered set of contacts
                bViewContactEntryForm:  false,
                bShowDeptSelection:     true,
                bShowOrgSelection:      true
             }
        },

        methods:  {
            useFormat(date) {
                const day = date.getDate();
                const month = date.getMonth() + 1;
                const year = date.getFullYear();
                return `${month}/${day}/${year}`;
            },
            // FUNCTION - toggles contact entry form
            toggleContactEntryForm() {
                this.bViewContactEntryForm = !this.bViewContactEntryForm
            },
            // required select
            updateCollegeCodeSelection() {
                // filter departments based on whether the associated college code on the 
                // department is that selected

                // since fields are prepopulate and user is changing affiliation, reset the viewboxes
                this.bShowDeptSelection = false
                this.bShowOrgSelection  = false
                // overwrite the preloaded selectors
                // codeSelection['college'] is valid
                this.codeSelection['college-code']  =   ""
                this.codeSelection['department']    =   ""
                this.codeSelection['dept-code']     =   ""
                this.codeSelection['div/org']       =   ""
                this.codeSelection['orgcode']       =   ""
                this.aFilteredOrgCodes              =   []
               
                let selectedCollege = this.aCollegeCodes.filter( (item)  => { return item['name'] == this.codeSelection['college']})[0]
                this.codeSelection['college-code'] = selectedCollege['code']

                this.aFilteredDeptCodes = this.aDeptCodes.filter ((item) => {return item['college-code'] == this.codeSelection['college-code']})
                this.aFilteredDeptCodes.sort((a, b) => {
                    // check department preferences for sorts
                    var codeA
                    var codeB
                    if(this.bSortByName) {
                        codeA = a['name']
                        codeB = b['name']
                    }
                    else {
                        codeA = a['dept-code']
                        codeB = b['dept-code']
                    }               
                    if (codeA < codeB) {
                        return -1
                    }
                    if (codeA > codeB) {
                        return 1
                    }
                    // names must be equal
                    return 0
                });

                // toggle the dept selection form element
                this.bShowDeptSelection = true

                //console.log(this.sSelectedCollege)
                //console.log("filtered dc: " + JSON.stringify(this.aFilteredDeptCodes))
                //console.log("ViewEditEmployeeEntire:: updateCollegeCodeSelection: codeSelection: "+JSON.stringify(this.codeSelection))
            },
            // required select
            updateDeptCodeSelection() {
                // fires when user selects a department code from list
                // filter orgs based on selected department codes
                // aSelectedDepts is array of dept codes
                //
                this.bShowOrgSelection              = false
                this.codeSelection['div/org']       = ""
                this.codeSelection['orgcode']       = ""

                let selectedDept = this.aDeptCodes.filter( (item)  => { return item['name'] == this.codeSelection['department']})[0]
                this.codeSelection['dept-code'] = selectedDept['dept-code']


                for ( let key in this.aOrgCodes)  {
                    let oItem = this.aOrgCodes[key]
                    if(oItem['dept-code'] == this.codeSelection['dept-code'])    {
                        this.aFilteredOrgCodes.push(oItem);  
                    }
                }
                this.aFilteredOrgCodes.sort((a, b) => {
                    var codeA
                    var codeB
                    if(this.bSortByName) {
                        codeA = a['name']
                        codeB = b['name']
                    }
                    else {
                        var codeA = a['org']
                        var codeB = b['org']
                    }                        
                    if (codeA < codeB) {
                        return -1
                    }
                    if (codeA > codeB) {
                        return 1
                    }
                    // names must be equal
                    return 0
                });

                // toggle the org code selection
                this.bShowOrgSelection = true

            },
            updateOrgCodeSelection()    {

                // reserved for potential action
                let selectedDivision = this.aOrgCodes.filter( (item)  => { return item['name'] == this.codeSelection['div/org']})
                this.codeSelection['orgcode'] = selectedDivision[0]['org']
                this.$emit('setEmplAffiliation', this.employee, this.codeSelection)
            },
            updateDefaultRole() {
                this.$emit('setEmplDefaultRole', this.emplDefaultRole)
            },
            deleteContact(employee, idx) {

                // fire store action
                this.$emit('deleteEmplContact', employee, idx)
                // reset the add form Boolean
                this.bViewContactEntryForm = false
            },
            addContact(employee, c) {
                // fire store action
                this.$emit('addEmplContact', employee, c)
                // reset the add form Boolean
                this.aContact = {
                    'type':                     '',     
                    'handle':                   '',
                    'value':                    '',
                    'reference':                ''
                }
                this.bViewContactEntryForm = false
            },
            cancelAddContact() {
                // reset the add form Boolean
                this.aContact = {
                    'type':                     '',     
                    'handle':                   '',
                    'value':                    '',
                    'reference':                ''
                }
                this.bViewContactEntryForm = false
            },
           
            setAllowedOrgs() {
                // argumnet is comma delimited list of orgs as string
                this.$emit('setAllowedOrgs', this.sOrgs)
            },

            // dropdown selector must be preloaded based on selected codes from employee
            // e.g, college-code, dept-code and org-code may be known, but the selection system filters
            // need to be preset
            // uses codeSelection, e.g. this.codeSelection so call in this context
            //
            preLoadAffiliation() {

                console.log("ViewEditEmployeeEntire:: preLoadAffiliation: fired")
                // for an employee the org code will always be present, as home-org
                if (this.codeSelection['orgcode'] !== null) {
                    // obtain the div/org name - return org object
                    let extractedOrg = this.aOrgCodes.filter ((item) => {
                        return item['org'] == this.codeSelection['orgcode'].substring(0,3)
                    })[0]  
                    // pull name and parent department code
                    if(typeof extractedOrg !== 'undefined')  {
                        this.codeSelection['div/org']       = extractedOrg['name']
                        this.codeSelection['dept-code']     = extractedOrg['dept-code']

                        // update filters on dept-code for orgs
                        this.aFilteredOrgCodes = this.aOrgCodes.filter ((item) => { 
                            return item['dept-code'] == this.codeSelection['dept-code']
                        })

                        // use department code to access the department table - returns department object
                        let extractedDept = this.aDeptCodes.filter ((item) => {
                            return item['dept-code'] == this.codeSelection['dept-code']
                        })[0]
                        // pull department name and college code
                        if(typeof extractedDept !== 'undefined')  {
                            this.codeSelection['department']    ==  extractedDept['name']
                            this.codeSelection['college-code'] ==  extractedDept['college-code']

                            //use college code to setup filters for departments
                            this.aFilteredDeptCodes = this.aDeptCodes.filter ((item) => {return item['college-code'] == extractedDept['college-code']})
                            this.aFilteredDeptCodes.sort((a, b) => {
                                // check department preferences for sorts
                                var codeA
                                var codeB
                                if(this.bSortByName) {
                                    codeA = a['name']
                                    codeB = b['name']
                                }
                                else {
                                    codeA = a['dept-code']
                                    codeB = b['dept-code']
                                }               
                                if (codeA < codeB) {
                                    return -1
                                }
                                if (codeA > codeB) {
                                    return 1
                                }
                                // names must be equal
                                return 0
                            });
                            let extractedCollege = this.aCollegeCodes.filter ((item) => {
                                return item['code'] == this.codeSelection['college-code']
                            })[0]

                            // get the college name
                            if(typeof extractedCollege !== 'undefined')  {
                                this.codeSelection['college'] = extractedCollege['name']
                            }
                        }
                    }      
                }     
            }  // end of function

            
        },

        // hooks
        beforeMount() {


            const {getOIDOnUserPrincipalName, getOIDOnUserDisplayName, userResults, graphError } = useGraphAPI()
            const { checkAccessToken }   = useAuthAPI()

            // check user ms-object-id.  If not set, attempt to get it from graph.
            if(!this.employee['ms-object-id'])   {
                console.log("VEEmployeeEntire:: setup: Found null ms-object-id on employee")
                // use only if editedEmployee['ms-object-id'] is not available

                // use one of two methods: on netid (emplPrincipleName) or displayName
                // MARK: Obtain object-id for this employee from graph
                if(this.employee.netid.length != 0) {
                    let emplPrincipalName = ""
                    if(this.employee.contacts.length != 0) {
                        // email exists
                        emplPrincipalName = this.employee.contacts.filter( (c) =>     {
                            return c.type == "EMAIL"
                        })[0].value
                    }
                    else emplPrincipalName = this.employee.netid + "@illinois.edu"

                    console.log("VEEmployeeEntire:: setup: emplPrincipalName: "+emplPrincipalName)
                    console.log("VEEmployeeEntire:: setup: gat: "+this.gat)

                    // use principalName to get OID from graph
                    //checkAccessToken()
                    getOIDOnUserPrincipalName(this.gat, emplPrincipalName)
                    .then( ()  =>   {
                        // set ms-object-id on store
                        console.log("VEEmployeeEntire:: setup: Graph response object id: "+JSON.stringify(userResults.value.id))
                        this.$emit('setOID', userResults.value.id)
                    })
                    .catch( () => {
                        this.bGraphError = graphError.value
                    })
                }
                else {
                    // no netid so likely no email and therefore no principal name
                    // us dispay name
                    //checkAccessToken()
                    getOIDOnUserDisplayName(this.gat, this.employee['name-concat'])
                    .then( ()  =>   {
                        // set ms-object-id on store
                        console.log("VEEmployeeEntire:: setup: Graph response object id: "+JSON.stringify(userResults.value.id))
                        this.$emit('setOID', userResults.value.id)
                    })
                    .catch( () => {
                        this.bGraphError = graphError.value
                    })

                }
            }

          
        },
        mounted()   {
        },        
        updated() {
        }
    }
</script>

<style scoped>

/*  generalized flex layout and structure */
    ._master_container   {
        width: 90vw;
        height: 90%;
        margin: 0 auto;
        padding: 0;
        box-sizing: border-box;
    }
    ._container_r1 {
        width: 100%;
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        justify-content: flex-start;
        margin: 10px 0 0 0;
        padding: 0;
    }
    ._container_r2 {
        width: 100%;
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        justify-content: flex-start;
        margin: 10px 0 0 0;
        padding: 0;
    }
    ._container_r3 {
        width: 100%;
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        justify-content: flex-start;
        margin: 10px 0 0 0;
        padding: 0;
    }
    ._container_r4 {
        width: 100%;
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        justify-content: flex-start;
        margin: 10px 0 0 0;
        padding: 0;
    }
    ._container_r5 {
        width: 100%;
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        justify-content: flex-start;
        margin: 10px 0 0 0;
        padding: 0;
    }
   
    ._employee_image {
        /* employee image or default photo size to colum flex specified size */
        width: 40%;
        margin: 0 auto 0;
        padding: 0px;
    }
    ._p_employee_name {
        width: 80%;
        margin: 50px auto 0;
        padding: 0;
        font-size: 12pt;
        font-weight: bold;
        color: #13294B;
    }
    ._date_picker {
        --dp-font-size: 0.8em;
        --dp-input-padding: 6px 10px 6px 10px;
        --dp-text-color: #13294B;
    }

    /*  basic flex structure - two sets per row */
    ._fieldset    {
        /* break width into two even chunks */
        width: 50%;
        margin: 0 25px 0;
        padding: 0;
    }
    ._p_fieldset_name  {
        /* names fieldset across top above any box */
        margin: 3px 0;
        padding: 2px;
        color: #13294B;
        font-size: 0.8em;
        font-weight: bold;
        text-align: left;
    }
    ._fieldset_box {
        width: 100%;
        border-style: solid;
        border-width: 2px;
        border-color: #13294B;
        background-color: #BFC9D9;
        margin: 0;
        padding: 3px 10px;
    }
    ._fieldset_box_eligibility {
        width: 100%;
        border-style: solid;
        border-width: 2px;
        border-color: #13294B;
        background-color:#f9da98;
        text-align: left;
        margin: 0;
        padding: 3px 10px;
    }
    ._fieldset_box_admin {
        border-style: solid;
        border-width: 2px;
        border-color: #13294B;
        background-color:#f9da98;
        margin: 0;
        padding: 3px 10px;
    }
    /* basic table structure to align inputs and labels     */
    ._fieldset_table {
        display: table;
        /* width is 100% of fieldset width */
        width: 100%;
        margin: 0;
        padding: 2px 7px;
    }
    ._fieldset_row {
        display: table-row;
        /* width is 100% of fieldset width */       
    }
    ._fieldset_cell_label {
        display: table-cell;
        width: 30%;
        /* width is fractional % of fieldset width */
        text-align: left;
        padding: 2px 5px;
        
    }
    ._fieldset_cell_select {
        /* selector from dropdown in width specified cell */
        display: table-cell;
        /* width is fractional % of fieldset width */
        text-align: left;
        margin: 0;
        padding: 2px 5px;
        
    }
    ._fieldset_cell_input {
        display: table-cell;
        width: 70%;
        /* width is fractional % of fieldset width */
        margin: 0;
        padding: 2px 5px;
    }
    ._fieldset_cell_zone_selector {
        display: table-cell;
        width: 70%;
        padding: 2px 10px;
        margin: 0;
        font-size: 0.9em;
        background-color:antiquewhite;
        color: black;
    } 
     
  
    /*  styling for existing contact management */
    ._contacts_box {
        /* area for existing contacts */
        margin: 0;
        padding: 2px;
        background-color:#BFC9D9;
        font-size: 0.8em;
        font-weight: normal;
        color: black;
    }
    ._contacts_table {
        display: table;
    }
    ._contacts_row {
        display: table-row;
        width: 100%;
    }
    ._contacts_cell_type {
        display: table-cell;
        width: 30%;
        text-transform: uppercase;
        font-size: 0.8em;
        font-weight: normal;
        color: black;
        text-align: left;
        padding: 0 25px 0 0;
    }
    ._contacts_cell_value {
        display: table-cell;
        width: 45%;
        text-transform: none;
        font-size: 0.9em;
        font-weight: bold;
        color: #13294B;
        text-align: left;
        padding: 0 2px 0;
    }
    ._contacts_cell_delete {
        width: 25%;
        display: table-cell;
        font-size: 0.6em;
        text-transform: uppercase;
        letter-spacing: 1px;
        font-weight: bold;
        color: #FF5F05;
        text-align: left;
        padding: 2px;
    }
    ._contacts_cell_delete:hover {
        background-color: #FF5F05;
        color: white;    
    }
 
    /* styling for new contact form */
    ._contacts_add_request     {
        margin: 0 0 0 10px;
        padding: 0;
        font-size: 0.8em;
        font-weight: bold;
        color: #13294B;
        text-align: left;
    }
    ._form     {
        margin: 0;
        padding: 0;
    }
    ._control_left_align {
        margin: 0px 0 0 10px;
        padding: 0;
        font-size: 0.8em;
        font-weight: bold;
        color: #13294B;
        text-align: left;     
    }
    ._contact_entry_line  {
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        justify-content: flex-start;
        margin: 0 2px 10px;
        padding: 0;   
    }
    ._contact {
        margin: 0;
        padding: 0;
    }
    ._entry_label {
        font-size: 0.8em;
        font-weight: normal;
        color: #13294B;
        text-align: left;
        padding: 0 0 0 5px;
        
    }
    ._entry_value {
        font-size: 0.8em;
        font-weight: normal;
        color: black;
        text-align: left;
        margin: 0 0 0 5px;
    }
    ._inline_icon {
        display: inline;
        padding: 0px 25px 0px 0px;;
        /* padding: 0 2px 0 3px; */
        height: 16px;
    }
    ._inline_icon_right {
        display: inline;
        height: 10px;
        margin: 0 10px 0 0;
    }
    ._inline_icon_left {
        display: inline;
        height: 10px;
        margin: 0 0 0 10px;
    }
    ._contact_types {
        /* styling for contact type list box */ 
        margin: 5px 0 5px;
        font-size: 0.8em;
        text-transform: uppercase;
        font-weight: normal;
        color: black;
    }
    ._inline-right-align {
        float: right;
    }
    ._inline-icon-right {
        height: 10px;
        margin: 0 10px 0 0;
    }
    /*  eligibility criteria - conditioned on config    */
    ._check {
        display: inline;
        width: 25px;
        margin: 2px 10px 0 0;
        padding: 0;  
    }
    ._p_annotation {
        font-size: 0.7em;
        color:green;
        text-align: left;
        margin: 2px 0 1px;
        padding: 0 2px;
    }
    ._p_annotation_2 {
        font-size: 0.7em;
        color:green;
        text-align: left;
        margin: 2px 0 1px;
        padding: 0 0 0 5px;
    }
    ._graph_error {
        font-size: 1.2em;
        color:#FF5F05;
        font-weight: bold;
    }
    ._p_annotation_3 {
        font-size: 0.9em;
        font-weight: bold;
        color: #13294B;
        text-align: left;
        margin: 2px 0 1px;
        padding: 0 10px 0 5px;
    }
    ._p_diminished {
        /* specifies the CSS props for the uuid information */
        text-align: left;
        font-size:  0.8em;
        font-weight: normal;
        color: black;
        margin: 0;
        padding: 2px;
    }
    ._p_diminished_grn {
        text-align: left;
        font-size:  0.8em;
        color: #006230;
        margin: 0;
        padding: 2px;
    }
    ._employees_controls {
        margin: 0;
        padding: 0;
    }
    ._employees_inline_controls {
        display: inline;
        margin: 0;
        padding: 0;
    }
    /* general controls styling */
    ._main_button {
        background-color: #13294B;
        color: white;
        font-weight: bold;
        border-style: solid;
        border-width: 1px;
        border-radius: 4px;
        border-color: #13294B;
        margin: 5px 10px;
        padding: 5px;
    }
    ._main_button:hover {
        background-color: #FF5F05;
        color: white;
    }
    ._contact_button_delete {
        background-color: #FF5F05;
        color: white;
        font-size: 0.6em;
        font-weight: normal;
        border-style: solid;
        border-width: 1px;
        border-radius: 3px;
        border-color: #13294B;
        margin: 1px;
        padding: 2px 10px;
    }
    ._contact_button_delete:hover {
        background-color: white;
        color: red;
    }
    ._contact_button_add {
        background-color: #006230;     
        color: white;
        font-size: 0.6em;
        font-weight: normal;
        border-style: solid;
        border-width: 1px;
        border-radius: 3px;
        border-color: #13294B;
        margin: 1px;
        padding: 2px 10px;
    }
    ._contact_button_add:hover {
        background-color: #FF5F05;     
        color: white;
    }
    ._contact_button_cancel {
        background-color: #13294B;     
        color: white;
        font-size: 0.6em;
        font-weight: normal;
        border-style: solid;
        border-width: 1px;
        border-radius: 3px;
        border-color: #13294B;
        margin: 1px;
        padding: 2px 10px;
    }
    ._contact_button_cancel:hover {
        background-color: #FF5F05;     
        color: white;
    }
    /* text styling for responsive form */
    ._col_8 {
        float: left;
        width:8%;
        margin-top: 5px;
    }
    ._col_10 {
        float: left;
        width:10%;
        margin-top: 5px;
    }
    ._col_15 {
        float: left;
        width:15%;
        margin-top: 5px;
    }
    ._col_20 {
        float: left;
        width:20%;
        margin-top: 5px;
    }
    ._col_25 {
        float: left;
        width:25%;
        margin-top: 5px;
    }
    ._col_30 {
        float: left;
        width:30%;
        margin-top: 5px;
    }
    ._col_40 {
        float: left;
        text-align: left;
        width: 40%;
        margin-top: 5px;
    }
    ._col_45 {
        float: left;
        text-align: left;
        width: 45%;
        margin-top: 5px;
    }
    ._col_50 {
        float: left;
        text-align: left;
        width: 50%;
        margin-top: 5px;
    }
    ._col_55 {
        float: left;
        text-align: left;
        width: 55%;
        margin-top: 5px;
    }

    ._col_60 {
        float: left;
        width: 60%;
        margin-top: 5px;
    }
    ._col_90 {
        float: left;
        width: 90%;
        margin-top: 5px;
    }
    ._row {
        text-align: left;
    }
    ._row:after {
        content: "";
        display: table;
        clear: both;
    }
    ._input, ._select, ._type_list {
        max-width: 100%;
        margin: 0 10px 0 0;
        padding: 2px 0 0 15px;
        font-size: 0.8em;
        border-style: solid;
        border-width: 1px;
        border-color: black;
        background-color: white;
        color: black;
        box-sizing: border-box; 
        resize: vertical;
    }
    ._type_list:disabled {
        color: green;
        font-weight: bold;
    }
    ._input:disabled {
        color: green;
    }
  
    ._input_label {
        display: inline-block;  
        color: blue;
        font-weight: bold;
        font-size: 0.8em;
        text-align: left;
        padding: 2px 0;  
    }
    ._input_label:after {
        content:":";
    }
   
    ._check_label {
        color: blue;
        font-weight: bold;
        font-size: 0.8em;
        text-align: left;
        margin: 2px 0;
        padding: 2px 0;
    }
    ._checkbox {
        display: inline;
        width: 25px;
        margin: 2px 10px 0 0;
        padding: 0;   
    }
    ._p_contact {
        display: inline;
        text-transform: none;
        font-size: 0.9em;
        font-weight: bold;
        color: blue;
        text-align: left;
        margin: 0;
        padding: 0 5px 0; 
    }
    ._p_contact_delete {
        display: inline;
        text-transform: none;
        font-size: 0.9em;
        font-weight: normal;
        color: red;
        text-align: left;
        margin: 0;
        padding: 0; 
    }
    ._inline_icon {
        display:inline;
        height: 10px;
        margin: 0;
        padding: 0 3px 0 0;
    }
    ._p_contact_delete:hover {
        color: blue;
        font-weight: normal;
    }
    @media screen and (max-width: 620px) {
        ._col_20, ._col_25, ._col_30, ._col_40, ._col_50, ._col_60  {
            width: 100%;
            margin-top: 0;
        }
        ._fieldset_box_admin {
            width: 100%;
            margin-top: 0;
        }
        /*
        ._input, ._select {
            width: 100%;
            margin: o 10px;
            padding: 2px 5px;
            margin-top: 0;
        }*/
    }
    @media screen and (max-width: 800px) {
        ._container_r1, 
        ._container_r2,
        ._container_r3,
        ._container_r4,
        ._container_r5 {
            flex-wrap: wrap;  
        }
    }
    /*
    @media print {
        ._check {
            font-size: 10pt;
            color: black;
            content: "O";
        }
        ._check:checked {
            font-size: 10pt;
            color: blue;
            content: "X";
        }
    }
        */
    
 
</style>